import React, { useState, useCallback, useEffect, useRef } from 'react';
import {
  Table,
  Button,
  Input,
  Space,
  Popconfirm,
  Typography,
  Card,
  Modal,
  Form,
  Select,
  Tooltip,
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  EyeOutlined,
  PushpinOutlined,
} from '@ant-design/icons';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import { SourceStory } from '../../types';
import { sourceStoriesApi, CreateSourceStoryRequest, UpdateSourceStoryRequest, usersApi } from '../../services/api';
import { useApi, useAsyncOperation } from '../../hooks';
import { formatDate, truncateText } from '../../utils';
import { PAGE_SIZE } from '../../constants';

const { Title } = Typography;
const { Search } = Input;

const PINNED_SOURCE_STORY_IDS_KEY = 'talebox_admin_source_stories_pinned_ids';
const MAX_PINNED_STORIES = 50;

const TABLE_PAGE_SIZE_OPTIONS = [10, 20, 50, 100] as const;
const MIN_TABLE_PAGE_SIZE = 1;
const MAX_TABLE_PAGE_SIZE = 100;

function clampTablePageSize(n: number): number {
  if (Number.isNaN(n) || n < MIN_TABLE_PAGE_SIZE) return PAGE_SIZE;
  return Math.min(n, MAX_TABLE_PAGE_SIZE);
}

function readPinnedIdsFromStorage(): string[] {
  try {
    const raw = localStorage.getItem(PINNED_SOURCE_STORY_IDS_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as unknown;
    return Array.isArray(parsed) ? parsed.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

const SourceStories: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  const [searchText, setSearchText] = useState<string>(() => searchParams.get('name') || '');
  const [createdByFilter, setCreatedByFilter] = useState<string>(() => searchParams.get('createdBy') || '');
  const [currentPage, setCurrentPage] = useState<number>(() => {
    const page = parseInt(searchParams.get('page') || '1', 10);
    return Number.isNaN(page) || page <= 0 ? 1 : page;
  });
  const [tablePageSize, setTablePageSize] = useState<number>(() =>
    clampTablePageSize(parseInt(searchParams.get('pageSize') || String(PAGE_SIZE), 10))
  );
  const [pinnedIds, setPinnedIds] = useState<string[]>(readPinnedIdsFromStorage);
  const [creatorOptions, setCreatorOptions] = useState<{ label: string; value: string }[]>([]);

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [editingStory, setEditingStory] = useState<SourceStory | null>(null);
  const [form] = Form.useForm();

  const { loading: asyncLoading, execute: executeAsync } = useAsyncOperation();

  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const params = new URLSearchParams();
    if (searchText) params.set('name', searchText);
    if (createdByFilter) params.set('createdBy', createdByFilter);
    if (currentPage > 1) params.set('page', String(currentPage));
    if (tablePageSize !== PAGE_SIZE) params.set('pageSize', String(tablePageSize));

    setSearchParams(params, { replace: true });
  }, [searchText, createdByFilter, currentPage, tablePageSize, setSearchParams]);

  useEffect(() => {
    let cancelled = false;
    usersApi
      .getUsers({ pageIndex: 0, pageSize: 500 })
      .then((res) => {
        if (cancelled) return;
        const opts = res.data
          .map((u) => {
            const name = u.userName ?? u.username ?? '';
            if (!name) return null;
            const label = u.fullName ? `${u.fullName} (${name})` : name;
            return { value: name, label };
          })
          .filter((x): x is { value: string; label: string } => x !== null);
        setCreatorOptions(opts);
      })
      .catch(() => {
        if (!cancelled) setCreatorOptions([]);
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const getSourceStories = useCallback(() => sourceStoriesApi.getSourceStories({
    pageIndex: currentPage - 1,
    pageSize: tablePageSize,
    name: searchText || undefined,
    createdBy: createdByFilter || undefined,
    pinnedIds: pinnedIds.length > 0 ? pinnedIds.join(',') : undefined,
  }), [currentPage, tablePageSize, searchText, createdByFilter, pinnedIds]);

  const {
    data: storiesData,
    loading: storiesLoading,
    refresh: refreshStories,
  } = useApi(getSourceStories, { immediate: true });

  useEffect(() => {
    refreshStories();
  }, [refreshStories, searchText, createdByFilter, currentPage, tablePageSize, pinnedIds]);

  const handleSearch = useCallback((value: string) => {
    setSearchText(value);
    setCurrentPage(1);
  }, []);

  const togglePin = useCallback((id: string) => {
    setPinnedIds((prev) => {
      const next = prev.includes(id)
        ? prev.filter((x) => x !== id)
        : [id, ...prev.filter((x) => x !== id)].slice(0, MAX_PINNED_STORIES);
      localStorage.setItem(PINNED_SOURCE_STORY_IDS_KEY, JSON.stringify(next));
      return next;
    });
  }, []);

  const pinnedRows = storiesData?.pinned ?? [];

  const handleReset = useCallback(() => {
    setSearchText('');
    setCreatedByFilter('');
    setCurrentPage(1);
    setTablePageSize(PAGE_SIZE);
    form.resetFields();
    refreshStories();
  }, [form, refreshStories]);

  const handleCreate = () => {
    setEditingStory(null);
    form.resetFields();
    setIsModalVisible(true);
  };

  const handleEdit = (story: SourceStory) => {
    setEditingStory(story);
    form.setFieldsValue({
      name: story.name,
      author: story.author,
      link: story.link,
    });
    setIsModalVisible(true);
  };

  const handleDelete = async (id: string) => {
    const result = await executeAsync(
      async () => {
        await sourceStoriesApi.deleteSourceStory(id);
      },
      { successMessage: 'Source story deleted successfully!' }
    );
    if (result === null) return;
    setPinnedIds((prev) => {
      if (!prev.includes(id)) return prev;
      const next = prev.filter((x) => x !== id);
      localStorage.setItem(PINNED_SOURCE_STORY_IDS_KEY, JSON.stringify(next));
      return next;
    });
    refreshStories();
  };

  const handleSubmit = async (values: CreateSourceStoryRequest) => {
    const operation = editingStory
      ? async () => {
          const response = await sourceStoriesApi.updateSourceStory(editingStory.id, values as UpdateSourceStoryRequest);
          return response.data;
        }
      : async () => {
          const response = await sourceStoriesApi.createSourceStory(values);
          return response.data;
        };

    const result = await executeAsync(
      operation,
      {
        successMessage: editingStory
          ? 'Source story updated successfully!'
          : 'Source story created successfully!'
      }
    );

    if (result) {
      setIsModalVisible(false);
      form.resetFields();
      refreshStories();
    }
  };

  const columns = [
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      render: (text: string, record: SourceStory) => (
        <Link to={`/source-stories/${record.id}`}>
          {truncateText(text, 50)}
        </Link>
      ),
    },
    {
      title: 'Author',
      dataIndex: 'author',
      key: 'author',
      render: (text: string) => text || '-',
    },
    {
      title: 'Link',
      dataIndex: 'link',
      key: 'link',
      render: (link: string) => link ? (
        <a href={link} target="_blank" rel="noopener noreferrer">
          {truncateText(link, 40)}
        </a>
      ) : '-',
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => formatDate(date),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
      render: (text: string) => text || '-',
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_: unknown, record: SourceStory) => (
        <Space>
          <Tooltip title={pinnedIds.includes(record.id) ? 'Unpin' : 'Pin to top (always visible above the list)'}>
            <Button
              type="text"
              icon={<PushpinOutlined />}
              onClick={() => togglePin(record.id)}
              style={{ color: pinnedIds.includes(record.id) ? '#1890ff' : undefined }}
              aria-label={pinnedIds.includes(record.id) ? 'Unpin' : 'Pin'}
            />
          </Tooltip>
          <Button
            type="text"
            icon={<EyeOutlined />}
            onClick={() => navigate(`/source-stories/${record.id}`)}
            title="View Details"
          />
          <Button
            type="text"
            icon={<EditOutlined />}
            onClick={() => handleEdit(record)}
            title="Edit"
          />
          <Popconfirm
            title="Are you sure you want to delete this source story?"
            description="All chapters will also be deleted."
            onConfirm={() => handleDelete(record.id)}
            okText="Yes"
            cancelText="No"
          >
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              title="Delete"
            />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        flexWrap: 'wrap',
        gap: 16,
      }}>
        <div>
          <Title level={2} style={{ margin: 0 }}>
            Content Management
          </Title>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={handleCreate}
          size="large"
          style={{
            background: '#1890ff',
            borderRadius: '6px',
            height: '40px',
            fontSize: '14px',
            fontWeight: '500',
          }}
        >
          Create Source Story
        </Button>
      </div>

      <Card
        style={{
          marginBottom: 24,
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
          gap: 16,
          marginBottom: 16,
        }}>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Name
            </label>
            <Search
              placeholder="Search by name..."
              allowClear
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              onSearch={handleSearch}
              style={{ width: '100%' }}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 8, fontWeight: '500', fontSize: '14px' }}>
              Created by
            </label>
            <Select
              allowClear
              showSearch
              placeholder="Filter by creator..."
              optionFilterProp="label"
              value={createdByFilter || undefined}
              onChange={(v) => {
                setCreatedByFilter(v ?? '');
                setCurrentPage(1);
              }}
              options={creatorOptions}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        <div style={{ display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
          <Button
            onClick={handleReset}
            style={{
              borderRadius: '6px',
              height: '36px',
              fontSize: '14px',
            }}
          >
            Reset
          </Button>
        </div>
      </Card>

      <Card
        style={{
          borderRadius: '8px',
          boxShadow: '0 1px 2px rgba(0, 0, 0, 0.03)',
        }}
      >
        <Table
          columns={columns}
          dataSource={[...pinnedRows, ...(storiesData?.data ?? [])]}
          rowKey="id"
          loading={storiesLoading}
          pagination={{
            current: currentPage,
            total: storiesData?.totalRecord || 0,
            pageSize: tablePageSize,
            showSizeChanger: true,
            pageSizeOptions: TABLE_PAGE_SIZE_OPTIONS.map(String),
            onChange: (page) => {
              setCurrentPage(page);
            },
            onShowSizeChange: (_page, size) => {
              setTablePageSize(size);
              setCurrentPage(1);
            },
            showTotal: (total, range) =>
              `${range[0]}-${range[1]} of ${total} stories`,
          }}
          scroll={{ x: 900 }}
          style={{
            fontSize: '14px',
          }}
        />
      </Card>

      <Modal
        title={editingStory ? 'Edit Source Story' : 'Create New Source Story'}
        open={isModalVisible}
        onCancel={() => {
          setIsModalVisible(false);
          form.resetFields();
        }}
        footer={null}
        width={500}
      >
        <Form
          form={form}
          layout="vertical"
          onFinish={handleSubmit}
        >
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Please enter name!' }]}
          >
            <Input placeholder="Enter story name..." />
          </Form.Item>

          <Form.Item
            name="author"
            label="Author"
          >
            <Input placeholder="Enter author name..." />
          </Form.Item>

          <Form.Item
            name="link"
            label="Link"
          >
            <Input placeholder="Enter original story link..." />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, textAlign: 'right' }}>
            <Space>
              <Button onClick={() => setIsModalVisible(false)}>
                Cancel
              </Button>
              <Button
                type="primary"
                htmlType="submit"
                loading={asyncLoading}
              >
                {editingStory ? 'Update' : 'Create'}
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default SourceStories;
